import React, { useEffect, useState } from 'react';
import { kpiAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import NotificationBell from '../components/NotificationBell';
import { ArrowRightOnRectangleIcon, HomeIcon, InformationCircleIcon, XMarkIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import InfoTooltip from '../components/InfoTooltip';
import { useAuth } from '../context/AuthContext';

export default function MyKpi() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [cycles, setCycles] = useState([]);
  const [cycleId, setCycleId] = useState('');
  const [submission, setSubmission] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ratingsMap, setRatingsMap] = useState({}); // itemId -> score (1-5)

  useEffect(()=>{ (async()=>{ try{ const { data } = await kpiAPI.listCycles(); setCycles(data); if(data?.length){ setCycleId(data[0].id); } } catch(e){} })(); },[]);

  const ensureSubmission = async (cid) => {
    if (!cid) return;
    setLoading(true);
    try {
      const { data } = await kpiAPI.getOrCreateMySubmission(cid);
      setSubmission(data);
      // Load items if exist (to show template items as locked)
      try {
        const full = await kpiAPI.getSubmissionById(data.id);
        if (full?.data) {
          setSubmission(full.data);
        }
        if (full?.data?.items) {
          // Separate template items from user-added items
          setItems((full.data.items || []).filter(i => !i.isTemplate));
          // Initialize ratings for existing template items if not set
          const next = { ...ratingsMap };
          for (const it of (full.data.items || [])) {
            if (it.isTemplate && next[it.id] == null) next[it.id] = 3;
          }
          setRatingsMap(next);
        }
      } catch (_) {}
    } catch(e){ toast.error(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  };

  useEffect(()=>{ ensureSubmission(cycleId); },[cycleId]);

  const addItem = () => setItems([ ...items, { title:'', description:'', weight: 0, targetValue:'', resultValue:'' } ]);
  const removeItem = (idx) => setItems(items.filter((_,i)=>i!==idx));

  const saveItems = async () => {
    if (!submission) return;
    try {
      await kpiAPI.upsertItems(submission.id, items);
      toast.success('Items saved');
    } catch(e){ toast.error(e.response?.data?.error || e.message); }
  };

  const submitSelf = async () => {
    if (!submission) return;
    try {
      // Save current non-template items first to ensure IDs exist
      await kpiAPI.upsertItems(submission.id, items);
      const full = await kpiAPI.getSubmissionById(submission.id);
      const allItems = full?.data?.items || [];
      const ratings = allItems.map(it => ({ itemId: it.id, score: Number(ratingsMap[it.id] || 3), comments: '' }));
      await kpiAPI.submitSelfAssessment(submission.id, { ratings, overallComment: '' });
      setSubmission(full.data);
      toast.success('Self assessment submitted');
    } catch(e){ toast.error(e.response?.data?.error || e.message); }
  };

  return (
    <div>
      {/* Header - Modern Gradient Design - matching UserDashboard */}
      <div className="bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-600 shadow-2xl">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center py-4 sm:py-6 gap-4">
            <div className="flex items-center w-full sm:w-auto">
              <div className="flex-shrink-0">
                <div className="h-12 w-12 sm:h-16 sm:w-16 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center border-2 border-white/40 shadow-2xl ring-4 ring-white/10">
                  <ChartBarIcon className="h-8 w-8 sm:h-10 sm:w-10 text-white" />
                </div>
              </div>
              <div className="ml-3 sm:ml-5 flex-1">
                <h1 className="text-lg sm:text-2xl font-bold text-white drop-shadow-lg">
                  My KPI
                </h1>
                <p className="text-xs sm:text-sm text-white/90 mt-0.5 sm:mt-1 font-medium">
                  Add your KPI items and rate yourself. Admin defaults may be prefilled.
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/user-dashboard')}
                className="inline-flex items-center px-3 sm:px-5 py-2 sm:py-3 border-2 border-white/30 text-xs sm:text-sm font-semibold rounded-xl text-white bg-white/10 backdrop-blur-md hover:bg-white/20 focus:outline-none focus:ring-4 focus:ring-white/30 transition-all duration-200 shadow-xl hover:scale-105"
                title="Back to Dashboard"
              >
                <HomeIcon className="h-4 w-4 sm:h-5 sm:w-5 sm:mr-2" />
                <span className="hidden sm:inline">Dashboard</span>
              </button>
              <NotificationBell />
              <button
                onClick={logout}
                className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 border-2 border-white/30 text-xs sm:text-sm font-semibold rounded-xl text-white bg-white/10 backdrop-blur-md hover:bg-white/20 focus:outline-none focus:ring-4 focus:ring-white/30 transition-all duration-200 shadow-xl hover:scale-105 self-end sm:self-auto"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 mb-6 flex items-center gap-2 max-w-3xl mx-auto px-3 sm:px-0">
        <label className="text-sm text-gray-600">Cycle</label>
        <select className="border rounded px-2 py-1" value={cycleId} onChange={e=>setCycleId(e.target.value)}>
          {cycles.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      <div className="bg-white border rounded p-4 max-w-3xl mx-auto shadow-sm mb-8">
        {/* Rating scale card */}
        <div className="mb-4 p-3 border rounded bg-gray-50">
          <div className="text-sm font-semibold text-gray-800 mb-1">RATING SCALE</div>
          <ul className="text-xs text-gray-700 space-y-1 list-disc pl-5">
            <li><span className="font-medium">5 Exceptional</span>: Accomplishments consistently exceed expected job requirements.</li>
            <li><span className="font-medium">4 Above Average</span>: Performance sometimes exceeds satisfactory expectations.</li>
            <li><span className="font-medium">3 Satisfactory</span>: Meets established objectives in a satisfactory manner.</li>
            <li><span className="font-medium">2 Inconsistent</span>: Performance is inconsistent and needs improvement to meet job requirements.</li>
            <li><span className="font-medium">1 Unsatisfactory</span>: Performance is below expectations and requires immediate corrective action.</li>
          </ul>
        </div>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-3">
          <div className="font-medium">KPI Items</div>
          <button className="px-4 py-2 rounded border w-full sm:w-auto" onClick={addItem}>Add Item</button>
        </div>
        {/* User items first */}
        <div className="space-y-3">
          {items.map((it, idx) => (
            <div key={idx} className="p-3 border rounded bg-gray-50 relative">
              <button
                className="absolute top-2 right-2 inline-flex items-center justify-center h-6 w-6 rounded hover:bg-gray-100"
                onClick={()=>removeItem(idx)}
                title="Remove"
                aria-label="Remove item"
              >
                <XMarkIcon className="h-4 w-4 text-gray-600" />
              </button>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 items-start">
                <input className="border rounded px-3 py-3 md:col-span-2" placeholder="Title" value={it.title} onChange={e=>{ const arr=[...items]; arr[idx].title=e.target.value; setItems(arr); }} />
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-600">Self-Rating (1–5)</label>
                  <input
                    type="number"
                    min={1}
                    max={5}
                    className="border rounded px-4 py-2 w-24 text-center"
                    value={ratingsMap[it.id || `tmp-${idx}`] ?? 3}
                    onChange={e=>{
                      const v = Math.max(1, Math.min(5, Number(e.target.value||3)));
                      const key = it.id || `tmp-${idx}`;
                      setRatingsMap(prev => ({ ...prev, [key]: v }));
                    }}
                  />
                </div>
                <div></div>
              </div>
            </div>
          ))}
        </div>
        {/* Default items provided by Admin after user inputs */}
        {(submission?.items || []).some(i => i.isTemplate) && (
          <div className="mt-6">
            <div className="text-sm font-medium text-gray-700 mb-2">Default Items (provided by Admin)</div>
            <div className="space-y-3">
              {(submission.items || []).filter(i=>i.isTemplate).map(it => (
                <div key={it.id} className="p-3 border rounded bg-gray-50">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-medium text-gray-800">{it.title}</div>
                    <InfoTooltip text={it.description || 'No description'} />
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <label className="text-xs text-gray-600">Self-Rating (1–5)</label>
                    <input
                      type="number"
                      min={1}
                      max={5}
                      className="border rounded px-4 py-2 w-24 text-center"
                      value={ratingsMap[it.id] ?? 3}
                      onChange={e=>{
                        const v = Math.max(1, Math.min(5, Number(e.target.value||3)));
                        setRatingsMap(prev => ({ ...prev, [it.id]: v }));
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
          <button className="px-4 py-3 bg-indigo-600 text-white rounded w-full" onClick={saveItems}>Save Items</button>
          <button className="px-4 py-3 bg-green-600 text-white rounded w-full" onClick={submitSelf} disabled={!submission}>Submit Self Assessment</button>
        </div>
      </div>
    </div>
  );
}



